import type { Ref } from 'react';
import { useCallback, useState } from 'react';
import classNames from 'classnames';
import { ChevronDownIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import type { ParkingFilterGroup, ParkingPropertyFilter, ParkingPropertyFilters } from '@repo/domain';
import { BorderedCheckbox } from '@repo/ui';

import FilterSubPropertiesPopover, {
  type FilterSubPropertyEntry,
} from '@features/parkingMap/components/FilterSubPropertiesPopover';

import { parkingFilterDictionary } from '@shared/constants/parkingFilterDictionary';

const ARIA_LABEL_TRANSLATION_KEY_BY_GROUP_KEY: Record<ParkingFilterGroup, string> = {
  covered: 'parking_search:filter.covered_type',
  evCharging: 'parking_search:filter.ev_charging_power',
  secured: 'parking_search:filter.secured_type',
  size: 'parking_search:filter.parking_spot_size',
};

interface Props {
  groupKey: ParkingFilterGroup;
  testId: TestId;
  label: string;
  filters: ParkingPropertyFilters;
  onChange: (keys: ParkingPropertyFilter[], checked: boolean) => void;
}

export default function SubPropertyFilter({ groupKey, testId, label, filters, onChange }: Props) {
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const Icon = parkingFilterDictionary[groupKey].icon;
  const subProperties = Object.entries(parkingFilterDictionary[groupKey].subProperties) as FilterSubPropertyEntry[];
  const subPropertyKeys = subProperties.map((subProperty) => subProperty[0]);
  const checked = subPropertyKeys.some((key) => filters[key]);
  const ariaLabel = tr(ARIA_LABEL_TRANSLATION_KEY_BY_GROUP_KEY[groupKey]);

  const handleClosePopup = useCallback(() => setIsPopupOpen(false), []);

  const handleToggleFilter = (newValue: boolean) => {
    onChange(subPropertyKeys, newValue);
  };

  const handleToggleCaret = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsPopupOpen((previous) => !previous);
  };

  const handleToggleSubProperty = (subPropertyKey: ParkingPropertyFilter) => {
    onChange([subPropertyKey], !filters[subPropertyKey]);
  };

  const renderTrigger = (triggerRef: Ref<HTMLButtonElement>) => (
    <button
      ref={triggerRef}
      type="button"
      onClick={handleToggleCaret}
      aria-label={ariaLabel}
      aria-expanded={isPopupOpen}
      className="flex w-full cursor-pointer flex-row items-center gap-1.5 text-left"
      data-testid={testId.append('toggler')}
    >
      <span className="bg-primary border-stroke-base flex size-6 shrink-0 items-center justify-center rounded-lg border">
        <ChevronDownIcon className={classNames('text-body size-4 transition-transform', isPopupOpen && 'rotate-180')} />
      </span>
      <span>{label}</span>
    </button>
  );

  return (
    <div className="relative h-full">
      <BorderedCheckbox
        icon={Icon}
        checked={checked}
        onChange={handleToggleFilter}
        testId={testId}
        label={
          <FilterSubPropertiesPopover
            isOpen={isPopupOpen}
            onClose={handleClosePopup}
            renderTrigger={renderTrigger}
            testId={testId.append('popover')}
            subProperties={subProperties}
            filters={filters}
            onToggleSubProperty={handleToggleSubProperty}
          />
        }
        className="h-full"
      />
    </div>
  );
}
